import { isEqual, last, orderBy } from 'lodash';
import { Component } from 'react';
import { CartesianGrid, Label, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Locales } from 'constants/app.constant';
import { addDays, formatDate, toMoment } from 'helpers/datetime.helper';
import { chartConfig } from 'components/common/chart/config/chart.config';
import { AxisDomain, LegendType } from 'recharts/types/util/types';
import { CircleDot } from './CircleDot';
import { SquareDot } from './SquareDot';
import './line.chart.scss';

export interface CustomRenderLeftYAxisLinesProps {
  dataKeys: string[];
}

export const CustomTooltipLine = ({ payload }: any) => {
  if (!payload || payload.length === 0) return null;
  const dateTime = payload[0]?.payload?.dateTime;
  const validEntries = payload.filter(({ dataKey, payload: data }: any) => data?.[`${dataKey}Display`]);
  if (validEntries.length === 0) return null;
  return (
    <div className="custom-tooltip">
      <div className="u-els-padding chart-tooltip__wrapper">
        {validEntries.map(({ dataKey, payload: data, name }: any) => {
          const label = name ? name.charAt(0).toUpperCase() + name.slice(1) : '';
          return (
            <div key={dataKey} className="u-els-bold">
              {label ? `${label}: ` : ''}{data[`${dataKey}Display`]}
            </div>
          );
        })}
        <div className="qa-tooltip-datetime">{dateTime}</div>
      </div>
    </div>
  );
};

export interface HistoryLineChartProps {
  width?: number;
  height?: number;
  xLabel?: string;
  yLabel?: string;
  rightYLabel?: string;
  data: object & { date: string }[];
  dataKeys: string[];
  rightDataKeys?: string[];
  interval?: number;
  yDomain?: AxisDomain;
  rightYDomain?: AxisDomain;
  xAxisDataKey?: string;
  yAxisName?: object;
  dataKeyNames?: Record<string, string>;
  legendType?: string;
  strokeWidth?: number;
  dotFormat?: {
    stroke: string;
    strokeWidth: number;
  };
  locale?: Locales;
  customRenderLeftYAxisLinesProps?: ({ dataKeys }: CustomRenderLeftYAxisLinesProps) => JSX.Element[];
}

interface HistoryLineChartState {
  data: object & { date: string }[];
}

export const LEFT_Y_AXIS_ID = 'leftYAxis';
export const RIGHT_Y_AXIS_ID = 'rightYAxis';

class HistoryLineChart extends Component<HistoryLineChartProps, HistoryLineChartState> {
  isSameDay: boolean;

  static displayName = 'HistoryLineChart';

  constructor(props: Readonly<HistoryLineChartProps>) {
    super(props);
    const data = this.addDateForXAxis(this.props.data);
    this.state = {
      data: this.toChartData(data)
    };
  }

  componentDidUpdate(prevProps: Readonly<HistoryLineChartProps>): void {
    if (!isEqual(prevProps.data, this.props.data)) {
      const data = this.addDateForXAxis(this.props.data);
      this.setState({ data: this.toChartData(data) });
    }
  }

  toChartData = (data: object & { date: string }[]) => [...orderBy(data, 'dateTime')];

  addDateForXAxis = (data: object & { date: string }[]) => {
    const { locale } = this.props;
    this.isSameDay = this.isDataSetSameDay(data);
    if (this.isSameDay) {
      const appendDate = {
        date: formatDate({ date: addDays(data[0].date, 1).toDate(), locale }),
        generatedDataPoint: true
      };
      return [...data, appendDate];
    }

    return data;
  };

  isDataSetSameDay = (data: object & { date: string }[]): boolean => {
    if (data.length <= 1) {
      return true;
    }
    const orderedData = orderBy(data, 'date');
    const firstDay = toMoment(orderedData[0].date);
    const lastDay = toMoment(last(orderedData).date);
    return firstDay.isSame(lastDay, 'day');
  };

  renderYAxisLines = (dataKeys: string[], yAxisId: string) =>
    dataKeys &&
    dataKeys.map((dataKey, index) => {
      const { dataColors } = chartConfig;
      let stroke = index % 2 === 0 ? dataColors[0] : dataColors[1];
      let dot =
        index % 2 === 0 ? (
          <SquareDot />
        ) : (
          {
            stroke: chartConfig.dataColors[1],
            strokeWidth: 10
          }
        );
      if (yAxisId === RIGHT_Y_AXIS_ID) {
        [, stroke] = dataColors;
        dot = {
          stroke: chartConfig.dataColors[1],
          strokeWidth: 10
        };
      }
      if (this.props.dotFormat) {
        dot = <CircleDot {...this.props.dotFormat} />;
      }
      const legendType = this.props.legendType || (index % 2 === 0 && yAxisId !== RIGHT_Y_AXIS_ID ? 'square' : 'circle');
      return (
        <Line
          name={this.props.dataKeyNames?.[dataKey] ?? this.props.yAxisName?.[yAxisId]}
          yAxisId={yAxisId}
          key={dataKey}
          type="monotone"
          dataKey={dataKey}
          legendType={legendType as LegendType}
          stroke={this.props.dotFormat?.stroke || stroke}
          strokeWidth={this.props.strokeWidth !== null ? this.props.strokeWidth : 2}
          isAnimationActive={false}
          connectNulls
          dot={dot}
        />
      );
    });

  render() {
    const width = this.props.width || chartConfig.line.defaultWidth;
    const height = this.props.height || chartConfig.line.defaultHeight;
    const lineMargin = chartConfig.line.defaultMargin;
    const legendHeight = chartConfig.line.defaultLegendHeight;
    const { interval, xLabel, yLabel, rightYLabel, rightDataKeys, dataKeys, customRenderLeftYAxisLinesProps } = this.props;
    const { data } = this.state;
    const defaultYDomain = [(dataMin) => Math.floor(dataMin * 0.9), (dataMax) => Math.ceil(dataMax * 1.1)] as AxisDomain;
    const defaultRightYDomain = [(dataMin) => Math.floor(dataMin - 1), (dataMax) => Math.ceil(dataMax + 2)] as AxisDomain;
    const yDomain = this.props.yDomain || defaultYDomain;
    const rightYDomain = this.props.rightYDomain || defaultRightYDomain;

    const dataWithId = data.map((item, index) => ({ ...item, uniqueId: index }));

    return (
      <div style={{ width: width || '100%', height }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={dataWithId} margin={lineMargin} accessibilityLayer>
            <CartesianGrid vertical={false} />
            <XAxis
              axisLine={false}
              dataKey="uniqueId"
              tickFormatter={(value) => dataWithId[value].date}
              tickMargin={8}
              interval={this.isSameDay ? data.length - 2 : interval}
              padding={{ left: 50, right: 50 }}
              stroke={chartConfig.labelColor}
            >
              <Label value={xLabel ?? ''} offset={-20} position="insideBottom" />
            </XAxis>
            <YAxis yAxisId={LEFT_Y_AXIS_ID} axisLine={false} tickLine={false} stroke={chartConfig.labelColor} domain={yDomain}>
              <Label value={yLabel ?? ''} angle={-90} position="left" />
            </YAxis>
            {rightDataKeys && (
              <YAxis yAxisId={RIGHT_Y_AXIS_ID} axisLine={false} tickLine={false} stroke={chartConfig.labelColor} domain={rightYDomain} orientation="right">
                <Label value={rightYLabel ?? ''} angle={-90} position="right" offset={-10} />
              </YAxis>
            )}
            <Tooltip content={CustomTooltipLine} />
            <Legend verticalAlign="top" height={legendHeight} wrapperStyle={{ textTransform: 'capitalize' }} />
            {customRenderLeftYAxisLinesProps?.({
              dataKeys
            }) || this.renderYAxisLines(dataKeys, LEFT_Y_AXIS_ID)}
            {this.renderYAxisLines(rightDataKeys, RIGHT_Y_AXIS_ID)}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }
}

export default HistoryLineChart;
